import { useEffect, useState } from "react";
import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import {
  Box,
  Dialog,
  DialogPopup,
  DialogTitle,
  Text,
} from "@salestrak/ui";

/**
 * The admin console fills the viewport. `DialogPopup` is `max-w-lg` with no
 * height of its own, so a near-fullscreen surface declares both axes
 * explicitly and clears the cap. The 4rem is a 2rem inset a side.
 */
const ADMIN_POPUP = "overflow-hidden";
import { useAuth } from "#app/lib/auth-context";
import { readJWT } from "#app/lib/jwt";
import { AdminConsole } from "#app/components/admin/admin-console";

function Admin() {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [verified, setVerified] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function verify() {
      try {
        // `superuser` is a JWT claim — read it from the (refreshed) access token
        // instead of an extra /oAuth/me round-trip. Same source as the nav
        // header's admin entry point (site-header.tsx).
        const token = await getAccessToken();
        if (!token || cancelled) return;
        if (!cancelled) setVerified(readJWT(token).superuser === true);
      } catch {
        if (!cancelled) setVerified(false);
      }
    }
    verify();
    return () => {
      cancelled = true;
    };
  }, [getAccessToken]);

  if (verified === null) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Text size="sm" variant="muted">
          Verifying access…
        </Text>
      </Box>
    );
  }

  if (!verified) {
    return <Navigate to="/workspace-select" />;
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) navigate({ to: "/", replace: true });
      }}
    >
      <DialogPopup size="full" className={ADMIN_POPUP} showCloseButton={false}>
        <DialogTitle className="sr-only">SalesTrak Admin</DialogTitle>
        <AdminConsole onClose={() => navigate({ to: "/", replace: true })} />
      </DialogPopup>
    </Dialog>
  );
}

export const Route = createFileRoute("/(app)/admin")({ component: Admin });
